import React, { useState } from "react";
import {
  ChevronDown, ChevronRight, PlusCircle, MinusCircle, Ban, CheckCircle2, Ticket, Trash2, Search, Crown,
} from "lucide-react";
import { adminApi } from "../lib/api";
import { inr, timeFrom } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";

const BET_STATUS_CLS = {
  pending: "text-amber-400",
  won: "text-primary",
  lost: "text-red-400",
  cancelled: "text-muted-foreground",
};

export default function AdminUsers({ token, users, onChanged }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(null);
  const [userBets, setUserBets] = useState({});
  const [chipDialog, setChipDialog] = useState(null); // { user, mode: 'add'|'remove' }
  const [chipAmount, setChipAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const toggleExpand = async (u) => {
    if (expanded === u.id) { setExpanded(null); return; }
    setExpanded(u.id);
    if (!userBets[u.id]) {
      try {
        const bets = await adminApi.userBets(token, u.id);
        setUserBets((prev) => ({ ...prev, [u.id]: bets }));
      } catch {
        setUserBets((prev) => ({ ...prev, [u.id]: [] }));
      }
    }
  };

  const openChips = (user, mode) => { setChipDialog({ user, mode }); setChipAmount(""); };

  const submitChips = async () => {
    const n = Number(chipAmount);
    if (!n || n <= 0) { toast({ title: "Enter a valid amount" }); return; }
    const signed = chipDialog.mode === "add" ? n : -n;
    setBusy(true);
    try {
      await adminApi.adjustChips(token, chipDialog.user.id, signed);
      toast({
        title: chipDialog.mode === "add" ? "Chips added ✅" : "Chips removed ✅",
        description: `${inr(Math.abs(n))} ${chipDialog.mode === "add" ? "credited to" : "deducted from"} ${chipDialog.user.name}`,
      });
      setChipDialog(null);
      onChanged && onChanged();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const toggleVip = async (u) => {
    const vip = !u.vip;
    try {
      await adminApi.setVip(token, u.id, vip);
      toast({ title: vip ? "VIP badge granted 👑" : "VIP badge removed", description: u.name });
      onChanged && onChanged();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const toggleBlock = async (u) => {
    const blocked = u.status !== "blocked";
    try {
      await adminApi.blockUser(token, u.id, blocked);
      toast({ title: blocked ? "User blocked" : "User unblocked", description: u.name });
      onChanged && onChanged();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const deleteUser = async (u) => {
    const who = u.name || u.username || u.phone;
    if (!window.confirm(`Permanently DELETE ${who}?\n\nThis wipes the user and ALL their data (bets, transactions, deposits, withdrawals, bank accounts, game rounds, chats) from the database and frees up their phone/username. This cannot be undone.`)) return;
    try {
      const res = await adminApi.deleteUser(token, u.id);
      toast({ title: "User deleted ✅", description: `${res.user || who} and all their data were purged.` });
      if (expanded === u.id) setExpanded(null);
      onChanged && onChanged();
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.referralCode || "").toLowerCase().includes(q))
    : users;
  const CAP = 100;
  const shown = filtered.slice(0, CAP);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-semibold">Manage Users — {users.length} total</h3>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, @username, phone, ref"
            className="pl-9 h-9"
            data-testid="admin-user-search"
          />
        </div>
      </div>
      <div className="space-y-2">
        {shown.length === 0 && (
          <div className="rounded-xl border border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">No users match “{query}”.</div>
        )}
        {shown.map((u) => {
          const isOpen = expanded === u.id;
          const bets = userBets[u.id] || [];
          const blocked = u.status === "blocked";
          return (
            <div key={u.id} className="rounded-xl border border-border bg-card overflow-hidden">
              <button onClick={() => toggleExpand(u)} data-testid={`admin-user-row-${u.id}`} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors">
                {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="h-7 w-7 rounded-full bg-primary/15 ring-1 ring-primary/30 grid place-items-center text-primary font-semibold text-xs shrink-0">
                  {(u.name || "U").charAt(0).toUpperCase()}
                </div>
                <span className="font-semibold text-sm truncate">@{u.username || u.phone}</span>
                {u.vip && <Badge variant="outline" className="border-[#E0B84C]/50 text-[#E0B84C] h-4 px-1 text-[10px] shrink-0 gap-0.5"><Crown className="h-2.5 w-2.5 fill-[#E0B84C]" />VIP</Badge>}
                {blocked && <Badge variant="outline" className="border-red-500/40 text-red-400 h-4 px-1 text-[10px] shrink-0">Blocked</Badge>}
                <span className="ml-auto font-display font-bold text-primary text-sm shrink-0">{inr(u.balance)}</span>
              </button>

              {isOpen && (
                <div className="border-t border-border bg-secondary/30 px-4 sm:px-5 py-4">
                  {/* Full details + all actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{u.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">@{u.username || u.phone} · {u.bets} bets · ref {u.referralCode}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" className="bg-white/5 border-white/15 gap-1" onClick={() => openChips(u, "add")}>
                        <PlusCircle className="h-4 w-4 text-primary" /> Add
                      </Button>
                      <Button size="sm" variant="outline" className="bg-white/5 border-white/15 gap-1" onClick={() => openChips(u, "remove")}>
                        <MinusCircle className="h-4 w-4 text-amber-400" /> Remove
                      </Button>
                      <Button size="sm" variant="outline" className={`bg-white/5 border-white/15 gap-1 ${blocked ? "text-primary" : "text-red-400"}`} onClick={() => toggleBlock(u)}>
                        {blocked ? <><CheckCircle2 className="h-4 w-4" /> Unblock</> : <><Ban className="h-4 w-4" /> Block</>}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`admin-vip-${u.id}`} className={`gap-1 ${u.vip ? "bg-[#E0B84C]/15 border-[#E0B84C]/50 text-[#E0B84C] hover:bg-[#E0B84C]/25" : "bg-white/5 border-white/15 text-[#E0B84C]"}`} onClick={() => toggleVip(u)}>
                        <Crown className={`h-4 w-4 ${u.vip ? "fill-[#E0B84C]" : ""}`} /> {u.vip ? "Remove VIP" : "Make VIP"}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`admin-delete-user-${u.id}`} className="bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20 gap-1" onClick={() => deleteUser(u)}>
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Ticket className="h-4 w-4 text-primary" /> Bet history</div>
                  {bets.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-2">No bets placed by this user.</div>
                  ) : (
                    <div className="space-y-2">
                      {bets.map((b) => (
                        <div key={b.id} className="flex items-center justify-between rounded-lg bg-card border border-border px-4 py-2.5 text-sm">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{b.matchLabel} · {b.marketName}</div>
                            <div className="text-xs text-muted-foreground">{b.selectionLabel} @ {b.odds != null ? b.odds.toFixed(2) : "—"} · {timeFrom(b.placedAt)}</div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            <span className="text-muted-foreground">{inr(b.stake)}</span>
                            <span className={`font-semibold capitalize ${BET_STATUS_CLS[b.status] || ""}`}>{b.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length > CAP && (
          <div className="rounded-xl border border-border bg-card px-5 py-3 text-center text-xs text-muted-foreground" data-testid="admin-users-cap-note">
            Showing first {CAP} of {filtered.length}. Use search to narrow down.
          </div>
        )}
      </div>

      <Dialog open={!!chipDialog} onOpenChange={(o) => !o && setChipDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {chipDialog?.mode === "add" ? "Add chips" : "Remove chips"} — {chipDialog?.user?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="text-sm text-muted-foreground">
              Current balance: <span className="font-semibold text-foreground">{inr(chipDialog?.user?.balance || 0)}</span>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" min={0} value={chipAmount} onChange={(e) => setChipAmount(e.target.value)} placeholder="Enter chips" className="mt-1.5" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white/5 border-white/15" onClick={() => setChipDialog(null)}>Cancel</Button>
            <Button onClick={submitChips} disabled={busy} className="font-semibold">
              {chipDialog?.mode === "add" ? "Add chips" : "Remove chips"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
